import React, { useEffect, useState } from "react";

// ASSETS
import ArrowLeft from "assets/svg/ArrowDown.svg";
import { ReactComponent as NoDataDark } from "assets/svg/NoDataDark.svg";

// STYLES
import {
  BackToGuidesButton,
  BackToGuidesImage,
  BackToWrapper,
  Column,
  ColumnNoBreak,
  ContactCardContentWrapper,
  ContactCardTitleWrapper,
  ContactCardWrapper,
  ContactCardsWrapper,
  ContentSectionWrapper,
  HeaderSectionWrapper,
  InstitutionsContactContainer,
  NoDataContainer,
  SvgContainer,
  TitleSectionWrapper,
  TitleWrapper,
} from "./InstitutionsContactStyled";

// LIBRARIES
import usePagination from "hooks/usePagination";
import { useTranslation } from "react-i18next";

// MISC
import { IProps } from "./InstitutionsContactModel";
import { TextType } from "components/Atoms/CustomText/CustomTextModel";
import { COLORS } from "constants/variables";
import aboutService from "services/aboutService";

// REDUX
import { useSelector } from "react-redux";
import { accessibilityBiggerCursorSelector } from "slices/UISlice/uiSliceSelectors";

// COMPONENTS
import HeaderTab from "components/HeaderTab/HeaderTab";
import Pagination from "components/Atoms/Paginations/Pagination";
import CustomText from "components/Atoms/CustomText/CustomText";

const InstitutionsContact = (props: IProps) => {
  // PROPERTIES
  const ELEMENTS_PER_PAGE = 10;

  // STATE CONSTANTS
  const [institutions, setInstitutions] = useState<any>(null);

  // LIBRARY CONSTANTS
  const biggerCursor = useSelector(accessibilityBiggerCursorSelector);
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const { t } = useTranslation();

  // API REQUESTS

  // LIFE CYCLE
  useEffect(() => {
    aboutService
      .getInstitutions()
      .then((response) => {
        // console.log("getInstitutions response", response);
        setInstitutions(response);
      })
      .catch((err) => {
        console.log("getInstitutions error", err);
      });
  }, []);

  const getContactData = (headType) => {
    return (
      institutions?.data?.filter((item) => item.attributes.head === headType) ||
      []
    )
      .map((item) => ({
        id: item.id,
        name: item.attributes.title,
        address: item.attributes.address,
        phone1: item.attributes.phone1,
        email: item.attributes.email,
        website: item.attributes.website,
        county: item.attributes?.county || "",
        city: item.attributes?.city || "",
      }))
      .sort((a, b) => {
        if (a.county.toLowerCase() < b.county.toLowerCase()) return -1;
        if (a.county.toLowerCase() > b.county.toLowerCase()) return 1;
        if (a.city.toLowerCase() < b.city.toLowerCase()) return -1;
        if (a.city.toLowerCase() > b.city.toLowerCase()) return 1;
        return 0;
      });
  };

  const IGSUContactData = getContactData("IGSU");
  const IGAvContactData = getContactData("IGAv");
  const ISUContactData = getContactData("ISU");
  const UPUContactData = getContactData("UPU");
  const SalvamontContactData = getContactData("Salvamont");
  const SAJContactData = getContactData("SAJ");
  const SalvaspeoContactData = getContactData("Salvaspeo");

  const usePaginatedData = (data) => {
    return usePagination(data, ELEMENTS_PER_PAGE);
  };

  const {
    currentPage: currentIGSUContactPage,
    totalPages: IGSUContactPages,
    currentData: currentIGSUContact,
    handlePageChange: handleIGSUContactPageChange,
  } = usePaginatedData(IGSUContactData);

  const {
    currentPage: currentIGaVContactPage,
    totalPages: IGaVContactPages,
    currentData: currentIGaVContact,
    handlePageChange: handleIGaVContactPageChange,
  } = usePaginatedData(IGAvContactData);

  const {
    currentPage: currentISUContactPage,
    totalPages: ISUContactPages,
    currentData: currentISUContact,
    handlePageChange: handleISUContactPageChange,
  } = usePaginatedData(ISUContactData);

  const {
    currentPage: currentUPUContactPage,
    totalPages: UPUContactPages,
    currentData: currentUPUContact,
    handlePageChange: handleUPUContactPageChange,
  } = usePaginatedData(UPUContactData);

  const {
    currentPage: currentSalvamontContactPage,
    totalPages: SalvamontContactPages,
    currentData: currentSalvamontContact,
    handlePageChange: handleSalvamontContactPageChange,
  } = usePaginatedData(SalvamontContactData);

  const {
    currentPage: currentSAJContactPage,
    totalPages: SAJContactPages,
    currentData: currentSAJContact,
    handlePageChange: handleSAJContactPageChange,
  } = usePaginatedData(SAJContactData);

  const {
    currentPage: currentSalvaspeoContactPage,
    totalPages: SalvaspeoContactPages,
    currentData: currentSalvaspeoContact,
    handlePageChange: handleSalvaspeoContactPageChange,
  } = usePaginatedData(SalvaspeoContactData);

  const content = (tabContent) => {
    return (
      <ContactCardsWrapper>
        {tabContent?.map((contact) => (
          <ContactCardWrapper key={contact.id}>
            <ContactCardTitleWrapper>
              <CustomText
                type={TextType.H4bold}
                text={contact.name}
                color={COLORS.VividAuburn}
              />
            </ContactCardTitleWrapper>
            <ContactCardContentWrapper>
              {contact.address && (
                <Column>
                  <CustomText
                    type={TextType.H4}
                    text={t("view.about.contactInstitutions.adress")}
                    color={COLORS.MetalicSilver}
                  />
                  <CustomText
                    type={TextType.H4}
                    text={contact.address}
                    color={COLORS.RaisinBlack}
                  />
                </Column>
              )}
              {contact.phone1 &&
                (() => {
                  const [firstPart, ...restParts] = contact.phone1.split(",");
                  return (
                    <ColumnNoBreak>
                      <CustomText
                        type={TextType.H4}
                        text={t("view.about.contactInstitutions.phone")}
                        color={COLORS.MetalicSilver}
                      />
                      <CustomText
                        type={TextType.H4}
                        text={firstPart.trim()} // First part before the comma
                        color={COLORS.RaisinBlack}
                      />
                      {restParts.length > 0 && (
                        <CustomText
                          type={TextType.H4}
                          text={restParts.join(",").trim()} // Remaining part(s) after the first comma
                          color={COLORS.RaisinBlack}
                        />
                      )}
                    </ColumnNoBreak>
                  );
                })()}
              {contact.email &&
                (() => {
                  const [firstEmail, ...restEmails] = contact.email.split(",");

                  return (
                    <ColumnNoBreak>
                      <CustomText
                        type={TextType.H4}
                        text={t("view.about.contactInstitutions.email")}
                        color={COLORS.MetalicSilver}
                      />
                      <CustomText
                        type={TextType.H4}
                        text={firstEmail.trim()} // First email
                        color={COLORS.RaisinBlack}
                      />
                      {restEmails.length > 0 && (
                        <CustomText
                          type={TextType.H4}
                          text={restEmails.join(",").trim()} // Remaining emails
                          color={COLORS.RaisinBlack}
                        />
                      )}
                    </ColumnNoBreak>
                  );
                })()}
              {contact.website && (
                <ColumnNoBreak>
                  <CustomText
                    type={TextType.H4}
                    text={t("view.about.contactInstitutions.website")}
                    color={COLORS.MetalicSilver}
                  />
                  <a href={contact.website} target={"_blank"} rel="noreferrer">
                    <CustomText
                      type={TextType.H4}
                      text={contact.website}
                      color={COLORS.RaisinBlack}
                    />
                  </a>
                </ColumnNoBreak>
              )}
            </ContactCardContentWrapper>
          </ContactCardWrapper>
        ))}
      </ContactCardsWrapper>
    );
  };

  const tabs = [
    {
      id: 0,
      tabTitle: "IGSU",
      content: content(currentIGSUContact),
      totalPages: IGSUContactPages,
      currentPage: currentIGSUContactPage,
      handlePageChange: handleIGSUContactPageChange,
    },
    {
      id: 1,
      tabTitle: "IGAv",
      content: content(currentIGaVContact),
      totalPages: IGaVContactPages,
      currentPage: currentIGaVContactPage,
      handlePageChange: handleIGaVContactPageChange,
    },
    {
      id: 2,
      tabTitle: "ISU",
      content: content(currentISUContact),
      totalPages: ISUContactPages,
      currentPage: currentISUContactPage,
      handlePageChange: handleISUContactPageChange,
    },
    {
      id: 3,
      tabTitle: "UPU",
      content: content(currentUPUContact),
      totalPages: UPUContactPages,
      currentPage: currentUPUContactPage,
      handlePageChange: handleUPUContactPageChange,
    },
    {
      id: 4,
      tabTitle: "Salvamont",
      content: content(currentSalvamontContact),
      totalPages: SalvamontContactPages,
      currentPage: currentSalvamontContactPage,
      handlePageChange: handleSalvamontContactPageChange,
    },
    {
      id: 5,
      tabTitle: "SAJ",
      content: content(currentSAJContact),
      totalPages: SAJContactPages,
      currentPage: currentSAJContactPage,
      handlePageChange: handleSAJContactPageChange,
    },
    {
      id: 6,
      tabTitle: "Salvaspeo",
      content: content(currentSalvaspeoContact),
      totalPages: SalvaspeoContactPages,
      currentPage: currentSalvaspeoContactPage,
      handlePageChange: handleSalvaspeoContactPageChange,
    },
  ];

  // EVENT HANDLERS
  const handleTabSelect = (selectedTabId: number) => {
    setSelectedTab(selectedTabId);
  };

  return (
    <InstitutionsContactContainer>
      <TitleSectionWrapper>
        <BackToWrapper
          onClick={() => window.history.back()}
          biggerCursor={biggerCursor}
        >
          <BackToGuidesImage src={ArrowLeft} alt="backIcon" />
          <BackToGuidesButton>
            <CustomText
              text={t("view.about.contactInstitutions.aboutDSU")}
              type={TextType.H5bold}
              color={COLORS.MetalicSilver}
            />
          </BackToGuidesButton>
        </BackToWrapper>
        <TitleWrapper>
          <CustomText
            type={TextType.H2}
            text={t("view.about.contactInstitutions.title")}
          />
        </TitleWrapper>
      </TitleSectionWrapper>

      <HeaderSectionWrapper>
        <HeaderTab tabs={tabs} onTabSelect={handleTabSelect} />
      </HeaderSectionWrapper>
      <ContentSectionWrapper>
        {tabs[selectedTab].content.props.children?.length > 0 ? (
          tabs[selectedTab].content
        ) : (
          <NoDataContainer>
            <SvgContainer>
              <NoDataDark />
            </SvgContainer>
            <CustomText
              text={t("app.noData")}
              type={TextType.H4}
              color={COLORS.MetalicSilver}
            />
          </NoDataContainer>
        )}

        {tabs[selectedTab].totalPages > 1 && (
          <Pagination
            totalPages={tabs[selectedTab].totalPages}
            currentPage={tabs[selectedTab].currentPage}
            onPageChange={tabs[selectedTab].handlePageChange}
          />
        )}
      </ContentSectionWrapper>
    </InstitutionsContactContainer>
  );
};

export default InstitutionsContact;
